import React, { useState, useEffect } from "react";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { IProgram, ISpeaker, ISpeakerExtended } from "../../../interfaces/models/IEvent";
import { ProgramTypeEnum, SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import Trash from "../../../components/Atomos/Icons/EditMode/Trash";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import ChevronDown from "../../../components/Atomos/Icons/ChevronDown";
import "../Events.css";
import Close from "../../../components/Atomos/Icons/Close";
import SessionTab from "./SessionTab";
import PostEventTab from "./PostEventTab";
import { useSessionForm } from "../../../hooks/Events/useSessionForm";
import { timestampToDateString } from "../../../utils/dates";
import { getPersons } from "../../../Services/Person";
import { uploadImage } from "../../../utils/UploadImage";
import { Descendant } from "slate";
import { getCompanies } from "../../../Services/Company";
import { toastError } from "../../../utils/toast";
import { buildSessionSpeakers } from "../../../utils/buildSessionSpeakers";

const MISSING_FIELDS_TOAST =
  "Please fill in all required fields before saving.";
const INVALID_FIELDS_TOAST =
  "Some information is incorrect. Please review the form and try again.";

const serializeSlateContent = (
  descendants: Descendant[] | undefined
): string | undefined => {
  if (!descendants || !Array.isArray(descendants) || descendants.length === 0) {
    return undefined;
  }

  const isEmpty = descendants.every((node: any) => {
    if (node.type === "paragraph" && node.children) {
      return node.children.every((child: any) => !child.text || child.text.trim() === "");
    }
    return false;
  });

  if (isEmpty) {
    return undefined;
  }

  try {
    return JSON.stringify(descendants);
  } catch (error) {
    console.error("Error serializing Slate content:", error);
    return undefined;
  }
};

interface SessionFormModalProps {
  onClose: () => void;
  onSave: (session: Partial<IProgram>) => void;
  onDelete?: () => void;
  initialSession?: IProgram | null;
  eventStartDate?: number;
  eventEndDate?: number;
  eventId?: string;
}

const SessionFormModal: React.FC<SessionFormModalProps> = ({
  onClose,
  onSave,
  onDelete,
  initialSession,
  eventStartDate,
  eventEndDate,
  eventId,
}) => {
  const { firmData } = UseFirmList();
  const {
    sessionForm,
    updateSessionField,
    handleUpdatePresenter,
    handleRemovePresenter,
    handleUpdatePostEventPresenter,
    handleRemovePostEventPresenter,
  } = useSessionForm(initialSession);

  const [activeTab, setActiveTab] = useState<"session" | "postEvent">(
    "session"
  );

  const [validationErrors, setValidationErrors] = useState<{
    sessionTitle?: string;
    sessionDate?: string;
    startTime?: string;
    endTime?: string;
    registrationPageSessionTitle?: string;
  }>({});

  useEffect(() => {
    if (!initialSession && eventStartDate && !sessionForm.sessionDate) {
      const defaultDateString = timestampToDateString(eventStartDate);
      updateSessionField("sessionDate", defaultDateString);
    }
  }, [
    initialSession,
    eventStartDate,
    sessionForm.sessionDate,
    updateSessionField,
  ]);

  const [companyOptions, setCompanyOptions] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companies = await getCompanies();
        const formattedData = companies.map((company: any) => ({
          id: company.id,
          name: company.name || "Unknown Company",
        }));
        setCompanyOptions(formattedData);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanyOptions([]);
      }
    };

    fetchCompanies();
  }, []);

  const sessionTypeOptions = Object.entries(ProgramTypeEnum).map(
    ([key, value]) => ({
      id: key,
      name: value,
    })
  );

  const documentTypeOptions = [
    { id: "presentation", name: "Presentation" },
    { id: "additional", name: "Additional Document" },
  ];

  const speakerTypeOptions = [
    { key: "speaker", value: SpeakerTypeEnum.speaker },
    { key: "keynote", value: SpeakerTypeEnum.keynote },
    { key: "moderator", value: SpeakerTypeEnum.moderator },
    { key: "panelist", value: SpeakerTypeEnum.panelist },
    { key: "guest", value: SpeakerTypeEnum.guest },
  ];

  const firmOptions = firmData.map((firm: any) => ({
    id: firm.id,
    name: firm.name || firm.firmName || "Unknown Firm",
  }));

  const combineDateAndTime = (
    dateString: string,
    timeString: string
  ): number | null => {
    if (!dateString || !timeString) {
      return null;
    }

    const [month, day, year] = dateString.split("/");
    if (!month || !day || !year) {
      return null;
    }

    const [hours, minutes] = timeString.split(":");
    if (!hours || !minutes) {
      return null;
    }

    const date = new Date(
      Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hours, 10),
        parseInt(minutes, 10)
      )
    );

    return Math.floor(date.getTime() / 1000);
  };

  const handleSave = async () => {
    const errors: {
      sessionTitle?: string;
      sessionDate?: string;
      startTime?: string;
      endTime?: string;
      registrationPageSessionTitle?: string;
    } = {};

    if (!sessionForm.sessionTitle || sessionForm.sessionTitle.trim() === "") {
      errors.sessionTitle = "Session Title is required";
    }

    if (!sessionForm.sessionDate || sessionForm.sessionDate.trim() === "") {
      errors.sessionDate = "Session Date is required";
    }

    if (!sessionForm.startTime || sessionForm.startTime.trim() === "") {
      errors.startTime = "Start Time is required";
    }

    if (!sessionForm.endTime || sessionForm.endTime.trim() === "") {
      errors.endTime = "End Time is required";
    }

    if (
      sessionForm.requiresRegistration &&
      (!sessionForm.registrationPageSessionTitle ||
        sessionForm.registrationPageSessionTitle.trim() === "")
    ) {
      errors.registrationPageSessionTitle =
        "Registration Page Session Title is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setActiveTab("session");
      toastError(MISSING_FIELDS_TOAST);
      return;
    }

    const startTimestamp = combineDateAndTime(
      sessionForm.sessionDate,
      sessionForm.startTime
    );
    const endTimestamp = combineDateAndTime(
      sessionForm.sessionDate,
      sessionForm.endTime
    );

    if (!startTimestamp || !endTimestamp) {
      const timestampErrors: typeof errors = {};
      if (!startTimestamp) {
        timestampErrors.startTime = "Invalid";
      }
      if (!endTimestamp) {
        timestampErrors.endTime = "Invalid";
      }
      setValidationErrors(timestampErrors);
      setActiveTab("session");
      toastError(INVALID_FIELDS_TOAST);
      return;
    }

    if (startTimestamp >= endTimestamp) {
      setValidationErrors({
        endTime: "Invalid",
      });
      setActiveTab("session");
      toastError(INVALID_FIELDS_TOAST);
      return;
    }

    if (!eventId) {
      toastError(INVALID_FIELDS_TOAST);
      return;
    }

    setValidationErrors({});

    let imageUrl = sessionForm.sessionImageUrl;
    if (sessionForm.sessionImage instanceof File) {
      const uploadedUrl = await uploadImage(
        sessionForm.sessionImage,
        eventId,
        "event/sessions/"
      );
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      }
    }

    const documentUrls: string[] = [...(sessionForm.sessionDocumentUrls || [])];
    
    if (sessionForm.sessionDocuments && sessionForm.sessionDocuments.length > 0) {
      const uploadPromises = sessionForm.sessionDocuments.map(async (file) => {
        if (file instanceof File) {
          const uploadedUrl = await uploadImage(
            file,
            eventId,
            "event/sessions/documents/"
          );
          return uploadedUrl;
        }
        return null;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      uploadedUrls.forEach((url) => {
        if (url) {
          documentUrls.push(url);
        }
      });
    }

    const existingSpeakers = initialSession?.speakers ?? [];
    const existingPostEventSpeakers =
      (initialSession as { postEventSpeakers?: ISpeakerExtended[] })
        ?.postEventSpeakers ?? existingSpeakers;

    const speakers = await buildSessionSpeakers(
      sessionForm.presenters,
      existingSpeakers,
    );

    const postEventSpeakers =
      sessionForm.postEventPresenters &&
      sessionForm.postEventPresenters.length > 0
        ? await buildSessionSpeakers(
            sessionForm.postEventPresenters,
            existingPostEventSpeakers,
          )
        : speakers;

    const sessionData: Partial<IProgram> & {
      registrationPageSessionTitle?: string;
      postEventSpeakers?: ISpeakerExtended[];
    } = {
      id: initialSession?.id ?? crypto.randomUUID(),
      type: sessionForm.sessionType?.id as any,
      requiredRegistration: sessionForm.requiresRegistration,
      registrationPageSessionTitle:
        sessionForm.registrationPageSessionTitle || undefined,
      title: sessionForm.sessionTitle,
      startTime: startTimestamp,
      endTime: endTimestamp,
      venueName: sessionForm.venue || undefined,
      location: sessionForm.location || undefined,
      description: serializeSlateContent(sessionForm.sessionDescription),
      speakers: speakers,
      isPostEvent: sessionForm.addToPostEvent ? true : undefined,
      postEventDescription: serializeSlateContent(sessionForm.postEventDescription),
      postEventSpeakers: sessionForm.addToPostEvent ? postEventSpeakers : undefined,
      youtubeLink: sessionForm.youtubeUrl || undefined,
      takeaways:
        sessionForm.takeaways.filter((t) => t.trim() !== "") || undefined,
      video: imageUrl || undefined,
      documents: documentUrls.length > 0 ? documentUrls : undefined,
    };

    onSave(sessionData);
    onClose();
  };

  const handleDelete = () => {
    if (!initialSession) {
      onClose();
      return;
    }

    if (
      onDelete &&
      window.confirm("Are you sure you want to delete this session?")
    ) {
      onDelete();
      onClose();
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div className="events-tabs-container">
        <button
          onClick={() => setActiveTab("session")}
          className={
            activeTab === "session"
              ? "events-tab-button-active flex items-center gap-2"
              : "events-tab-button flex items-center gap-2"
          }
        >
          <span>SESSION</span>
          {activeTab === "session" ? (
            <ChevronDown className="fill-white w-[14px] h-[14px]" />
          ) : (
            <ChevronDown
              className="fill-gray-font w-[14px] h-[14px]"
              rotation="rotate(-90deg)"
            />
          )}
        </button>
        <button
          onClick={() => setActiveTab("postEvent")}
          className={
            activeTab === "postEvent"
              ? "events-tab-button-active flex items-center gap-2"
              : "events-tab-button flex items-center gap-2"
          }
        >
          <span>POST EVENT</span>
          {activeTab === "postEvent" ? (
            <ChevronDown className="fill-white w-[14px] h-[14px]" />
          ) : (
            <ChevronDown
              className="fill-gray-font w-[14px] h-[14px]"
              rotation="rotate(-90deg)"
            />
          )}
        </button>
      </div>

      <div className="h-px bg-gray-300 w-full"></div>

      <div className="flex flex-col gap-8 w-full mt-8">
        <div className="flex flex-col gap-6">
          <h2 className="text-[#0070CD] font-decimal text-[32px] font-medium leading-[36px] text-left">
            Session Details
          </h2>
        </div>

        {activeTab === "session" ? (
          <SessionTab
            eventId={eventId || ""}
            sessionForm={sessionForm}
            updateSessionField={updateSessionField}
            handleUpdatePresenter={handleUpdatePresenter}
            handleRemovePresenter={handleRemovePresenter}
            sessionTypeOptions={sessionTypeOptions}
            speakerTypeOptions={speakerTypeOptions}
            firmOptions={firmOptions}
            companyOptions={companyOptions}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            editingSession={initialSession}
            validationErrors={validationErrors}
            onFieldChange={(field) => {
              if (validationErrors[field as keyof typeof validationErrors]) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[field as keyof typeof validationErrors];
                  return newErrors;
                });
              }
            }}
          />
        ) : (
          <PostEventTab
            sessionForm={sessionForm}
            updateSessionField={updateSessionField}
            handleUpdatePresenter={handleUpdatePostEventPresenter}
            handleRemovePresenter={handleRemovePostEventPresenter}
            sessionTypeOptions={sessionTypeOptions}
            speakerTypeOptions={speakerTypeOptions}
            firmOptions={firmOptions}
            companyOptions={companyOptions}
            documentTypeOptions={documentTypeOptions}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
          />
        )}

        <div className="flex justify-between items-center pt-6">
          <LegacyButton
            text="DELETE SESSION"
            content="textIcon"
            icon={
              <div className="w-5 h-5 [&>svg>path]:fill-current ml-2">
                <Trash />
              </div>
            }
            color="delete"
            style="outlined"
            size="small"
            onClick={handleDelete}
          />
          <div className="flex gap-4">
            <LegacyButton
              text="CANCEL"
              content="textIcon"
              icon={
                <Close className="w-5 h-5 [&>svg>path]:fill-current ml-2" />
              }
              color="neutral"
              style="outlined"
              size="small"
              onClick={onClose}
            />
            <LegacyButton
              text="SAVE"
              content="textIcon"
              icon={
                <div className="w-5 h-5 [&>svg>path]:fill-current ml-2">
                  <SaveChanges />
                </div>
              }
              color="accent"
              style="filled"
              size="small"
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionFormModal;
